import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { ExxComError } from 'lib/classes/exxcom-error.class';
import { shouldSuppressPageView } from 'lib/services/google/gtm-pageview.util';
import { RouterService } from 'lib/services/router.service';

const scriptName = 'gtm.service';

declare let dataLayer: any;

// https://support.google.com/tagmanager/topic/6333310?hl=en&ref_topic=3002579

@Injectable()
export class GtmService {
    isBrowser: boolean;

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        private routerService: RouterService
    ) {
        try {
            this.isBrowser = isPlatformBrowser(this.platformId);
        } catch (err) {
            console.error(...new ExxComError(732888, scriptName, err).stamp());
        }
    }

    init() {
        try {
            // The GTM container tag is declared statically in each client's index.html
            // so Termly's resource-blocker can see it during its single unblocking pass.
            // Injecting it here raced Termly and lost once bootstrap slowed down (ST-3416).
            this.initPageViewTracking();
        } catch (err) {
            console.error(...new ExxComError(839984, scriptName, err).stamp());
        }
    }

    private initPageViewTracking() {
        try {
            if (!this.isBrowser) {
                return;
            }
            this.routerService.onRouteChange('gtmPageView', () => {
                this.trackPageView();
            });
        } catch (err) {
            console.error(...new ExxComError(839985, scriptName, err).stamp());
        }
    }

    trackPageView() {
        try {
            if (!this.isBrowser) {
                return;
            }
            // Scanner-mangled 404s (ten-char base64 last segment that fell through to `**`)
            // are not sessions. Internal /404 navigations match the `404` route and still count.
            if (shouldSuppressPageView(this.routerService.router.routerState?.snapshot?.root, window.location.pathname)) {
                return;
            }
            dataLayer.push({
                event: 'page_view',
                page_path: window.location.pathname,
                page_location: window.location.href,
                page_title: document.title,
            });
        } catch (err) {
            console.error(...new ExxComError(839986, scriptName, err).stamp());
        }
    }

    trackAddToCart(item: any, quantity: number | string) {
        try {
            if (!this.isBrowser) {
                return;
            }
            dataLayer.push({
                event: 'addToCart',
                data: {
                    name: item.description,
                    id: item.mpn,
                    price: item.price,
                    quantity: quantity,
                },
            });
        } catch (err) {
            console.error(...new ExxComError(839484, scriptName, err).stamp());
        }
    }

    trackCheckOutPages(path: string) {
        try {
            if (!this.isBrowser) {
                return;
            }
            dataLayer.push({
                event: path,
            });
        } catch (err) {
            console.error(...new ExxComError(839777, scriptName, err).stamp());
        }
    }

    trackGoals(formId: string) {
        try {
            if (!this.isBrowser) {
                return;
            }
            dataLayer.push({
                event: 'goalsTracking',
                data: {
                    category: 'marketoforms',
                    action: formId,
                    label: formId,
                },
            });
        } catch (err) {
            console.error(...new ExxComError(884532, scriptName, err).stamp());
        }
    }

    trackMarketoFormSubmission(formId: string, email: string, phone: string) {
        try {
            if (!this.isBrowser) {
                return;
            }
            dataLayer.push({
                event: 'marketoFormTracking',
                data: {
                    action: formId,
                    email: email,
                    phone_number: phone,
                },
            });
        } catch (err) {
            console.error(...new ExxComError(293992, scriptName, err).stamp());
        }
    }

    trackProductView(item: any) {
        try {
            if (!this.isBrowser) {
                return;
            }
            dataLayer.push({
                event: 'productView',
                data: {
                    id: item.mpn,
                    name: item.description,
                    variant: '',
                    price: item.price ? item.price.toFixed(2) : '',
                    category: item.category,
                    list: '',
                },
            });
        } catch (err) {
            console.error(...new ExxComError(938842, scriptName, err).stamp());
        }
    }

    trackTransaction(transaction: any) {
        try {
            if (!this.isBrowser) {
                return;
            }
            dataLayer.push({
                event: 'transaction',
                data: {
                    action: 'Transaction id: ' + transaction.refNumber,
                    transactionId: transaction.refNumber,
                    transactionAffiliation: '',
                    transactionSubTotal: '',
                    transactionTotal: transaction.amountTotal,
                    transactionTax: transaction.amountTax,
                    transactionShipping: transaction.amountShipping,
                    transactionCurrency: 'USD',
                    transactionProducts: transaction.lines.map((product: any) => {
                        return {
                            sku: product.item,
                            name: product.product.description,
                            category: product.product.category,
                            price: product.product.price,
                            quantity: product.quantity,
                            variant: '',
                        };
                    }),
                },
            });
        } catch (err) {
            console.error(...new ExxComError(439843, scriptName, err).stamp());
        }
    }
}
